import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import styled from "styled-components";
import { authenticationService } from "@/api/authentication";
import { useAuth } from "@/shared/contexts/AuthContext";
import {
	CREATOR_REGISTER_OAUTH_ERROR_KEY,
	CREATOR_REGISTER_OAUTH_NONCE_KEY,
	CREATOR_REGISTER_OAUTH_STATE_PREFIX,
	readRoleRegisterSession,
	writeRoleRegisterSession,
} from "@/features/auth";

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100vh;
	gap: 1rem;
`;

const ErrorText = styled.p`
	color: #ff4d4f;
	font-size: 0.9rem;
`;

// Back to role sign-up, keeping an invitation's link details.
const registerPath = (role?: string, extra: Record<string, string> = {}) => {
	const { invite, registrationRole } = readRoleRegisterSession();
	const params = new URLSearchParams();
	const pathRole = role ?? (invite ? registrationRole : undefined);
	if (pathRole) params.set("role", pathRole);
	if (invite) {
		params.set("inviteToken", invite.token);
		params.set("inviteEmail", invite.email);
	}
	Object.entries(extra).forEach(([key, value]) => params.set(key, value));
	const query = params.toString();
	return `/register${query ? `?${query}` : ""}`;
};

const GoogleCallback: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { login } = useAuth();
	const [error, setError] = useState<string | null>(null);
	const hasExchanged = useRef(false);

	useEffect(() => {
		if (hasExchanged.current) return;
		hasExchanged.current = true;

		const state = searchParams.get("state");
		const code = searchParams.get("code");
		if (!code) {
			const message = "No authorization code received from Google.";
			setError(message);
			if (state?.startsWith(CREATOR_REGISTER_OAUTH_STATE_PREFIX)) {
				sessionStorage.setItem(CREATOR_REGISTER_OAUTH_ERROR_KEY, message);
				setTimeout(() => navigate(registerPath()), 1500);
			} else {
				setTimeout(() => navigate("/login"), 3000);
			}
			return;
		}

		const redirectUri = `${window.location.origin}/auth/callback`;

		// `state` carries the pre-login destination (e.g. /creator-invitations/:token).
		// Only honor internal absolute paths to avoid open-redirect issues.
		if (state?.startsWith(CREATOR_REGISTER_OAUTH_STATE_PREFIX)) {
			const nonce = state.slice(CREATOR_REGISTER_OAUTH_STATE_PREFIX.length);
			const expectedNonce = sessionStorage.getItem(CREATOR_REGISTER_OAUTH_NONCE_KEY);
			if (!nonce || nonce !== expectedNonce) {
				setError("This Google signup session is invalid. Please try again.");
				setTimeout(() => navigate(registerPath()), 1500);
				return;
			}

			const current = readRoleRegisterSession();
			const registrationRole = current.registrationRole || "creator";
			authenticationService
				.exchangeGoogleSignup({ code, redirectUri, registrationRole })
				.then((response) => {
					writeRoleRegisterSession({
						...current,
						email: response.profile.email,
						emailVerified: true,
						google: {
							signupToken: response.signupToken,
							email: response.profile.email,
							expiresAt: Date.now() + response.expiresIn * 1000,
						},
					});
					sessionStorage.removeItem(CREATOR_REGISTER_OAUTH_NONCE_KEY);
					navigate(registerPath(registrationRole, { step: "details", provider: "google" }), {
						replace: true,
					});
				})
				.catch((err) => {
					const message = err.response?.data?.message || "Google signup failed. Please try again.";
					sessionStorage.setItem(CREATOR_REGISTER_OAUTH_ERROR_KEY, message);
					navigate(registerPath(), { replace: true });
				});
			return;
		}
		const returnTo = state && state.startsWith("/") && !state.startsWith("//") ? state : "/campaigns";

		authenticationService
			.googleLogin({ code, redirectUri })
			.then((response) => {
				login(response.user);
				navigate(returnTo, { replace: true });
			})
			.catch((err) => {
				const msg = err.response?.data?.message || "Google login failed. Please try again.";
				setError(msg);
				setTimeout(() => navigate("/login"), 3000);
			});
	}, [login, navigate, searchParams]);

	return (
		<Container>
			{error ? (
				<ErrorText>{error}</ErrorText>
			) : (
				<>
					<Spin size="large" />
					<p>Signing in with Google...</p>
				</>
			)}
		</Container>
	);
};

export default GoogleCallback;
